/**
 * Page context v2 — the single site-owned description of "which page am I on"
 * (COM-585).
 *
 * Before this module, four producers each decided independently that a
 * navigation had happened, and they disagreed:
 *
 *   - `SegmentPageView`        — pathname only
 *   - `AbTastyPageviewTracker` — pathname + search
 *   - `PageTypeTracker`        — page-type VALUE change (PLP→PLP dedupes away)
 *   - `RouteChangePerfTracker` — `navigation.state` transitions
 *
 * So a route-bound event could carry the initial document URL or a stale page
 * type. This module owns the decision and the context; the transports
 * (dataLayer push, Segment `page()`, AB Tasty hit) become subscribers.
 *
 * PURE ON PURPOSE: no `window`, no `document`, no router imports. The context
 * is built from values the caller already resolved, which keeps the
 * navigation rules unit-testable under the node environment and lets the SSR
 * inline script and the client publisher share one implementation.
 *
 * Design + decision register: docs/page-context-v2-design.md.
 */

/** Contract version, carried on every context message. */
export const PAGE_CONTEXT_VERSION = "v2";

/** Identifies the storefront that produced the context. */
export const PAGE_CONTEXT_HOST_VARIANT = "composable";

/** Emitted once, in the initial HTML, ahead of GTM and AB Tasty. */
export const INITIAL_PAGE_CONTEXT_EVENT = "hec_initial_page_context";

/** Emitted once per meaningful committed navigation, after hydration. */
export const PAGE_CONTEXT_READY_EVENT = "hec_page_context_ready";

/**
 * The resolved context for one page.
 *
 * `hec_page_type` is DELIBERATELY namespaced rather than reusing the bare
 * `ecomm_pagetype` / `pageType` keys the ticket's contract table names
 * (design D5). Those keys are owned by `pushPageType`
 * (`data-layer.client.ts:65`), which enforces a single-live-value invariant by
 * retroactively renaming every prior occurrence to `prev_*` — AB Tasty reads
 * the FIRST match in the append-only dataLayer, so a stale landing-page value
 * kept firing homepage campaigns everywhere (SFCC-11890). That rewrite is
 * incompatible with C4's requirement that each event immutably keep its own
 * route's page type: the next navigation would reach back and rewrite an
 * already-published context event. Namespacing keeps both invariants intact.
 */
export type PageContext = {
  hec_navigation_id: string;
  /** Exact URL including original query-value casing. */
  hec_page_location: string;
  hec_page_title: string;
  /** Namespaced page type — see the note above. `undefined` on routes that set none. */
  hec_page_type: string | undefined;
  /** Absent on the initial document navigation (the ticket marks it not required). */
  hec_previous_page_location?: string;
  hec_hostVariant: typeof PAGE_CONTEXT_HOST_VARIANT;
  hec_page_context_version: typeof PAGE_CONTEXT_VERSION;
};

export type PageContextEventName =
  | typeof INITIAL_PAGE_CONTEXT_EVENT
  | typeof PAGE_CONTEXT_READY_EVENT;

export type PageContextEvent = PageContext & { event: PageContextEventName };

/**
 * Query params whose VALUE change counts as a new page view (design D1/D2).
 *
 * Base rule is pathname-only, which is what `SegmentPageView` already does
 * today (`segment/page-view-tracker.tsx:44`) — deliberately excluding
 * filter/sort so the Workers storefront matches SFRA, where those refreshed
 * the grid over AJAX (`Search-UpdateGrid`) without a page view. That keeps C5
 * ("must not increase") and C6 ("one page call per meaningful route") holding
 * by construction.
 *
 * This matters more than it looks: PLP facet changes commit through
 * `navigate()` (`hec/context/cio-facet-adapter.tsx:215`), so they ARE real
 * committed navigations. Without this filter the publisher would fire on every
 * filter click, and filter/sort already emit their own `filterSort` /
 * `Product List Filtered` events.
 *
 * THE ONE APPROVED EXCEPTION (martech, 2026-09-26): a changed SEARCH TERM
 * counts as a new page. Searching "vitamin" then "bandage" is two distinct
 * pages to a shopper and to reporting, even though the pathname never moves.
 * Facets, sort and pagination stay excluded. `q` is the search-term param
 * (`components/search-input.tsx:87`, `product-list/shop.$categoryId.tsx:320`).
 *
 * Declared as a table — rather than inline logic — so martech can widen the
 * rule without a rewrite, the same reason `DATALAYER_FORKS`
 * (`event-layer/fork.ts:41`) exists. Adding a param here is the ONLY supported
 * way to make a query change meaningful.
 */
export const MEANINGFUL_NAVIGATION_QUERY_PARAMS: readonly string[] = ["q"];

/**
 * Collapse a URL to the identity that decides whether a navigation is
 * "meaningful": the pathname plus the values of any allowlisted query params.
 *
 * A trailing slash is normalized away (`/foo/` and `/foo` are the same page).
 * Allowlisted params are read in table order and ALWAYS appended, present or
 * not, so an absent param and an empty one collapse to the same key — `/` and
 * `/?q=` are one page, as they should be. Param ordering in the URL can
 * therefore never produce two keys for one page either. Values keep their
 * original casing: the contract requires the emitted `hec_page_location` to
 * preserve it, and the key must agree with what is emitted.
 *
 * The result is an opaque comparison key, never emitted anywhere — do not
 * parse it or assert on its exact shape.
 */
export function navigationKey(
  pathname: string,
  search = "",
  params: readonly string[] = MEANINGFUL_NAVIGATION_QUERY_PARAMS,
): string {
  const normalizedPath =
    pathname.length > 1 && pathname.endsWith("/")
      ? pathname.slice(0, -1)
      : pathname;
  if (params.length === 0) return normalizedPath;

  const search_ = new URLSearchParams(search);
  const parts = params.map(name => `${name}=${search_.get(name) ?? ""}`);
  return `${normalizedPath}?${parts.join("&")}`;
}

/**
 * `true` when a commit landing on `nextKey` should publish a new context.
 *
 * Interpretation note (flagged for martech sign-off): a re-navigation to the
 * SAME key does not republish. The ticket asks to "deduplicate repeated
 * publication of the same navigation, not every occurrence of a URL", which
 * argues for republishing; but `SegmentPageView` already dedupes on pathname
 * today, and C5/C6 are measured against that baseline, so republishing here
 * would show up as an INCREASE. A genuine A→B→A return still publishes,
 * because the key actually changes — which is what C3 requires.
 */
export function shouldPublishContext(
  lastPublishedKey: string | null,
  nextKey: string,
): boolean {
  return lastPublishedKey !== nextKey;
}

/**
 * Mints `hec_navigation_id`s for one document: `<documentId>:<ordinal>`,
 * ordinal 0 being the initial document navigation.
 *
 * Why not `location.key`: React Router REUSES a location's key when you go
 * back or forward to an earlier history entry, so a return to a previously
 * visited route would collide with its first visit — suppressing exactly the
 * republish C3 demands. A monotonic ordinal is unique per navigation INSTANCE,
 * and being ordered it also makes QA's 25-navigation count directly readable
 * off the dataLayer.
 *
 * The document id is minted server-side and embedded in the initial HTML so
 * the SSR context and every client-published context share one namespace.
 */
export function createNavigationIdFactory(
  documentId: string,
  startAt = INITIAL_NAVIGATION_ORDINAL,
): () => string {
  let ordinal = startAt;
  return () => navigationIdAt(documentId, ordinal++);
}

/**
 * The ordinal the SSR initial context always carries. The client publisher
 * starts at `INITIAL_NAVIGATION_ORDINAL + 1`, so the two halves of a document
 * form one unbroken sequence.
 */
export const INITIAL_NAVIGATION_ORDINAL = 0;

/** Compose a navigation id. The single place the `<id>:<ordinal>` shape lives. */
export function navigationIdAt(documentId: string, ordinal: number): string {
  return `${documentId}:${ordinal}`;
}

/** Parse `<documentId>:<ordinal>` back apart. `null` if malformed. */
export function parseNavigationId(
  navigationId: string,
): { documentId: string; ordinal: number } | null {
  const separator = navigationId.lastIndexOf(":");
  if (separator <= 0) return null;
  const rawOrdinal = navigationId.slice(separator + 1);
  // `Number("")` is 0, so an empty ordinal segment would otherwise parse as a
  // valid navigation 0.
  if (rawOrdinal === "") return null;
  const ordinal = Number(rawOrdinal);
  if (!Number.isInteger(ordinal) || ordinal < 0) return null;
  return { documentId: navigationId.slice(0, separator), ordinal };
}

/**
 * The document id and URL the client publisher continues from.
 *
 * On a regular document these are the loader's (`served`): the server minted
 * the id and embedded it in the initial context. On a document shell
 * (COM-482) the initial context resolved both in the browser — the shell is
 * one prerendered document shared by every guest — so the values the loader
 * carries belong to the live render that completed the shell, not to the
 * context the document actually published. The publisher has to continue
 * THAT one, or ordinal 1 would open a second namespace. Falls back to
 * `served` when nothing usable was published (the head script did not run).
 */
export function documentIdentityFrom(
  published:
    | Pick<PageContext, "hec_navigation_id" | "hec_page_location">
    | undefined,
  served: { documentId: string; documentUrl: string },
): { documentId: string; documentUrl: string } {
  const documentId =
    published && parseNavigationId(published.hec_navigation_id)?.documentId;
  if (!documentId || !published?.hec_page_location) return served;
  return { documentId, documentUrl: published.hec_page_location };
}

/**
 * Assemble a context message. Callers resolve the inputs; this owns only the
 * shape, so the SSR and client paths can never drift apart.
 */
export function buildPageContext(input: {
  navigationId: string;
  pageLocation: string;
  pageTitle: string;
  pageType: string | undefined;
  previousPageLocation?: string | null;
}): PageContext {
  const context: PageContext = {
    hec_navigation_id: input.navigationId,
    hec_page_location: input.pageLocation,
    hec_page_title: input.pageTitle,
    hec_page_type: input.pageType,
    hec_hostVariant: PAGE_CONTEXT_HOST_VARIANT,
    hec_page_context_version: PAGE_CONTEXT_VERSION,
  };
  // Omit rather than emit null: the ticket marks the field "not required" on
  // the initial navigation, and an absent key is unambiguous to a raw-array
  // reader in a way that `null` is not.
  if (input.previousPageLocation) {
    context.hec_previous_page_location = input.previousPageLocation;
  }
  return context;
}

/** Wrap a context as the dataLayer message for the given event name. */
export function toPageContextEvent(
  event: PageContextEventName,
  context: PageContext,
): PageContextEvent {
  return { event, ...context };
}

/**
 * Event names that carry the route context (C4).
 *
 * Deliberately an ALLOWLIST rather than "stamp everything". The excluded
 * pushes are user-scoped or lifecycle, not route-scoped — identity
 * (`userId`, `hashedEmail`, `customerLoyalty`, `customerGroupSalesforce`),
 * `heapId`, `appHydrated`, the click-source attribution push and the Apple Pay
 * funnel. Stamping those would add noise to the shadow diff martech has to
 * review without making any of them answerable per route.
 *
 * Contents are the ticket's five named route-bound events plus the fork keys
 * from `event-layer/fork.ts`, which are the rest of the route-bound surface.
 */
export const ROUTE_BOUND_EVENTS: ReadonlySet<string> = new Set([
  // The five named in COM-585 §3.
  "GA4_productDetail",
  "view_item_list",
  "view_cart",
  "search",
  "select_item",
  // Fork keys — the remaining route-bound surface (event-layer/fork.ts:20-35).
  "GA4_addToCart",
  "GA4_removeFromCart",
  "GA4_quickviewAddToCart",
  "GA4_productClick",
  "productClick",
  "add_to_wishlist",
  "GA4_search",
  "filterSort",
  "formInitiate",
  "formSubmit",
  "GA4_routeChange",
]);

/** The context fields copied onto a route-bound event. */
type StampedFields = Pick<
  PageContext,
  "hec_navigation_id" | "hec_page_location" | "hec_page_title" | "hec_page_type"
>;

/**
 * Stamp a route-bound event with the context of the route it occurred on (C4).
 *
 * Returns the event UNCHANGED — the same object reference — when it is not
 * route-bound, has no `event` name, no context is available yet, or is itself
 * a context message. Identity is part of the contract: a caller that gets its
 * own object back knows nothing was added, and `pushDataLayer` stays a
 * pass-through whenever the feature is off.
 *
 * Existing keys on the event WIN. A producer that already resolved its own
 * context for a specific reason must not be silently overwritten by the
 * ambient one.
 */
export function stampRouteContext<T extends Record<string, unknown>>(
  event: T,
  context: PageContext | undefined | null,
  routeBoundEvents: ReadonlySet<string> = ROUTE_BOUND_EVENTS,
): T | (T & StampedFields) {
  if (!context) return event;
  const name = event.event;
  if (typeof name !== "string" || !routeBoundEvents.has(name)) return event;

  const stamped: Record<string, unknown> = {
    hec_navigation_id: context.hec_navigation_id,
    hec_page_location: context.hec_page_location,
    hec_page_title: context.hec_page_title,
    hec_page_type: context.hec_page_type,
    ...event,
  };
  return stamped as T & StampedFields;
}
